import { setActiveCustomerTab } from '@/core/redux/commonSlice';
import { RootState } from '@/core/redux/store';
import { addCartItem, clearCart } from '@/core/redux/cartSlice';
import { getData, postData } from '@/services/api.service';
import { useEffect, useState } from 'react';
import { FaEye, FaTrash, FaRedo } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import Spinner from './spinner';
import ReactPaginate from 'react-paginate';
import { toast } from 'react-toastify';

const CustomerOrders = ({ setOrderId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );
  const getOrderList = async () => {
    setLoading(true);
    try {
      const response = await getData(
        `orders?status=0&limit=10&page=${page + 1}`
      );
      if (response.status == 200 || response.status == 201) {
        setOrders(response?.data?.data?.data);
        const totalItems = Number(response?.data?.data?.total);
        const pageSize = 10;
        setTotalPage(Math.ceil(totalItems / pageSize));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOrderId(null);
    getOrderList();
  }, [page]);

  const deleteOrder = async (orderId: number) => {
    const payload = {
      id: orderId,
    };
    try {
      const response = await postData('cancel_order', payload);
      if (response.status == 200 || response.status == 201) {
        getOrderList();
        toast.success(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const repeatOrder = async (orderId: number) => {
    try {
      // Clear existing cart before adding new items
      dispatch(clearCart());
      
      const response = await postData('repeat_order', { id: orderId });
      if (response.status == 200 || response.status == 201) {
        const orderData = response.data.data;
        
        // Add each product to the cart
        orderData.products.forEach((product: any) => {
          const cartItem = {
            id: product.id.toString(),
            name: product.name,
            price: product.unit_price,
            image: product.image,
            category_id: '1', // Default category since not provided in API response
            quantity: product.qty,
            service_mode: 'dine_in', // Default service mode
          };
          
          dispatch(addCartItem(cartItem));
        });
        
        toast.success(`Order repeated! ${orderData.products.length} products added to cart.`);
        
        // Navigate to cart page after adding products
        navigate('/cart');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to repeat order');
    }
  };

  return (
    <>
      <div className="row">
        {loading ? (
          <Spinner />
        ) : (
          <>
            <div className="col-md-12">
              {orders?.length > 0 ? (
                orders?.map((sale: any) => (
                  <div key={sale.id} className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <strong>Order ID- {sale.reference_no}</strong>
                          <span className="d-block">
                            {new Date(sale.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span
                            className={
                              sale?.sale_status === 3
                                ? 'status-red'
                                : sale?.sale_status === 2
                                  ? 'status-orange'
                                  : sale?.sale_status === 1
                                    ? 'status-green'
                                    : ''
                            }
                          >
                            {sale?.status}
                          </span>
                        </div>
                      </div>
                      <hr className="mt-3 mb-3" />
                      <div className="d-flex justify-content-between">
                        <div>
                          <h4 className="price">
                            {currencyCode}
                            &nbsp;{sale.grand_total}
                          </h4>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          <Link
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              repeatOrder(sale?.id);
                            }}
                            to="#"
                          >
                            Repeat Order
                          </Link>
                          <Link
                            className="btn btn-sm btn-success"
                            onClick={() => {
                              setOrderId(sale?.id);
                              dispatch(setActiveCustomerTab('orderDetails'));
                            }}
                            to="#"
                          >
                            <FaEye />
                          </Link>
                          {sale.sale_status === 2 && (
                            <Link
                              className="btn btn-sm btn-danger"
                              to="#"
                              onClick={() => {
                                deleteOrder(sale?.id);
                              }}
                            >
                              <FaTrash />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="card mb-5">
                  <div className="card-body">
                    <h3>You have not ordered anything yet!</h3>
                  </div>
                </div>
              )}
            </div>
            <div className="col-md-12">
              {orders?.length > 0 && (
                <ReactPaginate
                  pageCount={totalPage}
                  containerClassName="pagination"
                  activeClassName="active"
                  onPageChange={(data) => {
                    setPage(data.selected);
                  }}
                  forcePage={page}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CustomerOrders;
